// Update the QuestionModal.js with better options management
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Table } from 'react-bootstrap';
import { suggestVariableName, cleanVariableName, finalCleanVariableName, PRESET_QUESTIONS } from './questionUtils';
import './questions.css';
import { OptionsManager } from './OptionsManager';

export default function QuestionModal({ 
  show, 
  onHide, 
  onSave, 
  editingQuestion = null, 
  validateQuestion 
}) {
  const [step, setStep] = useState(1);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectedCustomType, setSelectedCustomType] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    variable_name: '',
    type: 'text_box',
    required: true,
    help_text: '',
    data: null,
    options: []
  });
  
  const [errors, setErrors] = useState({});
  const [isAutoSuggested, setIsAutoSuggested] = useState(true);

  // Initialize form when modal opens or editing question changes
  useEffect(() => {
    if (editingQuestion) {
      console.log('🔍 Editing question:', editingQuestion);
      setFormData({
        ...editingQuestion,
        options: editingQuestion.options || []
      });
      setIsAutoSuggested(false);
      setStep(2);
      
      const preset = PRESET_QUESTIONS.find(p => p.data === editingQuestion.data);
      setSelectedPreset(preset || null);
      
      if (editingQuestion.type === 'custom_dropdown') {
        setSelectedCustomType('custom_dropdown');
      } else if (editingQuestion.type === 'text_box') {
        setSelectedCustomType('text_box');
      }
    } else {
      resetForm();
    }
    setErrors({});
  }, [editingQuestion, show]);

  const resetForm = () => {
    setStep(1);
    setSelectedPreset(null);
    setSelectedCustomType(null);
    setFormData({
      label: '',
      variable_name: '',
      type: 'text_box',
      required: true,
      help_text: '',
      data: null,
      options: []
    });
    setIsAutoSuggested(true);
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setSelectedCustomType(null);
    setFormData({
      label: preset.default_question,
      variable_name: preset.variable_suggestion,
      variable_type: preset.variable_type,
      type: preset.type,
      required: true,
      help_text: '',
      data: preset.data,
      options: [],
      ...(preset.allow_custom ? { allow_custom: true } : {})
    });
    setIsAutoSuggested(true);
    setStep(2);
  };

  const handleCustomSelect = () => {
    setSelectedPreset(null);
    setSelectedCustomType('text_box');
    setFormData({
      label: '',
      variable_name: '',
      variable_type: 'custom',
      type: 'text_box',
      required: true,
      help_text: '',
      data: null,
      options: []
    });
    setIsAutoSuggested(true);
    setStep(2);
  };

  const handleCustomDropdownSelect = () => {
    setSelectedPreset(null);
    setSelectedCustomType('custom_dropdown');
    setFormData({
      label: '',
      variable_name: '',
      variable_type: 'custom',
      type: 'custom_dropdown',
      required: true,
      help_text: '',
      data: null,
      options: []
    });
    setIsAutoSuggested(true);
    setStep(2);
  };

  const handleLabelChange = (value) => {
    setFormData(prev => ({
      ...prev,
      label: value
    }));

    if (!editingQuestion && isAutoSuggested && !selectedPreset) {
      const suggested = suggestVariableName(value);
      setFormData(prev => ({
        ...prev,
        variable_name: suggested
      }));
    }
  };

  const handleVariableNameChange = (value) => {
    const cleaned = cleanVariableName(value);
    setFormData(prev => ({
      ...prev,
      variable_name: cleaned
    }));
    setIsAutoSuggested(false);
  };

  const handleVariableNameBlur = (value) => {
    const finalCleaned = finalCleanVariableName(value);
    setFormData(prev => ({
      ...prev,
      variable_name: finalCleaned
    }));
  };

  // NEW: Better options handling
  const handleOptionsChange = (newOptions) => {
    console.log('📝 Options changed:', newOptions);
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  // Improved form submission with better validation
  const handleSave = () => {
    console.log('💾 Saving question:', formData);
    
    // Validate form data
    const validationErrors = validateQuestion(formData);
    
    if (validationErrors) {
      console.log('❌ Validation errors:', validationErrors);
      setErrors(validationErrors);
      return;
    }

    // Additional validation and cleanup for custom dropdown
    if (formData.type === 'custom_dropdown') {
      const validOptions = formData.options.filter(opt => 
        opt && 
        typeof opt === 'object' && 
        opt.label && 
        opt.label.trim() && 
        opt.value && 
        opt.value.trim()
      );
      
      console.log('📋 Valid options:', validOptions);
      
      if (validOptions.length === 0) {
        setErrors({ options: 'At least one option is required for dropdown questions' });
        return;
      }
      
      // Clean up options and ensure unique values
      const cleanedOptions = validOptions.map(opt => ({
        label: opt.label.trim(),
        value: opt.value.trim()
      }));
      
      const uniqueValues = [...new Set(cleanedOptions.map(opt => opt.value))];
      if (cleanedOptions.length !== uniqueValues.length) {
        setErrors({ options: 'Option values must be unique' });
        return;
      }
      
      const finalFormData = {
        ...formData,
        options: cleanedOptions
      };
      
      console.log('✅ Final form data:', finalFormData);
      onSave(finalFormData);
    } else {
      // For non-dropdown questions, remove options
      const finalFormData = {
        ...formData,
        options: []
      };
      
      console.log('✅ Final form data (non-dropdown):', finalFormData);
      onSave(finalFormData);
    }
    
    handleClose();
  };

  const handleClose = () => {
    resetForm();
    setErrors({});
    onHide();
  };

  const handleBack = () => {
    setStep(1);
    setSelectedPreset(null);
    setSelectedCustomType(null);
    setErrors({});
  };

  const isEditing = !!editingQuestion;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Edit Question' : 'Add Question'}
          {step === 2 && !isEditing && (
            <Button variant="link" size="sm" onClick={handleBack} className="ms-2">
              ← Back
            </Button>
          )}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {step === 1 && !isEditing && (
          <>
            <h6 className="mb-3">What type of question do you want to add?</h6>
            
            <Row className="mb-4">
              <Col lg={6}>
                <Card 
                  className="mb-3 cursor-pointer hover-shadow h-100" 
                  onClick={handleCustomSelect}
                >
                  <Card.Body className="d-flex align-items-center">
                    <div className="me-3" style={{ fontSize: '2rem' }}>✏️</div>
                    <div>
                      <h6 className="mb-1">Text Input</h6>
                      <small className="text-muted">
                        Free-text input field for open-ended answers
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={6}>
                <Card 
                  className="mb-3 cursor-pointer hover-shadow h-100" 
                  onClick={handleCustomDropdownSelect}
                >
                  <Card.Body className="d-flex align-items-center">
                    <div className="me-3" style={{ fontSize: '2rem' }}>📋</div>
                    <div>
                      <h6 className="mb-1">Custom Dropdown</h6>
                      <small className="text-muted">
                        Dropdown with your own custom options
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <h6 className="mb-2">Or choose a system preset:</h6>
            <Row>
              {PRESET_QUESTIONS.map((preset) => (
                <Col lg={4} md={6} key={preset.id} className="mb-3">
                  <Card 
                    className="h-100 cursor-pointer hover-shadow"
                    onClick={() => handlePresetSelect(preset)}
                  >
                    <Card.Body className="text-center">
                      <div style={{ fontSize: '2rem' }} className="mb-2">
                        {preset.icon}
                      </div>
                      <h6 className="mb-1">{preset.label}</h6>
                      <small className="text-muted">
                        {preset.default_question}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}

        {step === 2 && (
          <>
            {/* Type indicators */}
            {selectedPreset && (
              <Alert variant="info" className="mb-3">
                <strong>{selectedPreset.icon} {selectedPreset.label}</strong>
                <div className="small">
                  This will create a dropdown populated with {selectedPreset.label.toLowerCase()} options
                  {selectedPreset.allow_custom && ' (users can also type their own)'}
                </div>
              </Alert>
            )}

            {selectedCustomType === 'text_box' && (
              <Alert variant="secondary" className="mb-3">
                <strong>✏️ Text Input</strong>
                <div className="small">This will create a text input field</div>
              </Alert>
            )}

            {selectedCustomType === 'custom_dropdown' && (
              <Alert variant="primary" className="mb-3">
                <strong>📋 Custom Dropdown</strong>
                <div className="small">This will create a dropdown with your custom options</div>
              </Alert>
            )}

            {/* Question Label */}
            <Form.Group className="mb-3">
              <Form.Label>Question Label</Form.Label>
              <Form.Control
                type="text"
                placeholder={
                  selectedPreset 
                    ? selectedPreset.default_question 
                    : selectedCustomType === 'custom_dropdown'
                      ? "What emotion should the scene convey?"
                      : "What feeling do you want to infuse?"
                }
                value={formData.label}
                onChange={(e) => handleLabelChange(e.target.value)}
                isInvalid={!!errors.label}
              />
              <Form.Control.Feedback type="invalid">
                {errors.label}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Variable Name */}
            <Form.Group className="mb-3">
              <Form.Label>
                Variable Name 
                <small className="text-muted ms-2">
                  Used in prompt as {`{{${formData.variable_name || 'variable_name'}}}`}
                </small>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={
                  selectedPreset 
                    ? selectedPreset.variable_suggestion 
                    : selectedCustomType === 'custom_dropdown'
                      ? "emotion"
                      : "feeling"
                }
                value={formData.variable_name}
                onChange={(e) => handleVariableNameChange(e.target.value)}
                onBlur={(e) => handleVariableNameBlur(e.target.value)}
                isInvalid={!!errors.variable_name}
                className={isAutoSuggested ? 'bg-warning-subtle' : ''}
              />
              {isAutoSuggested && (
                <Form.Text className="text-muted">
                  <small>Auto-suggested (you can edit this)</small>
                </Form.Text>
              )}
              <Form.Control.Feedback type="invalid">
                {errors.variable_name}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Custom Dropdown Options - NEW improved version */}
            {selectedCustomType === 'custom_dropdown' && (
              <Form.Group className="mb-3">
                <OptionsManager
                  options={formData.options}
                  onChange={handleOptionsChange}
                  errors={errors.options}
                />
              </Form.Group>
            )}

            {/* Required Checkbox */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Required"
                checked={formData.required}
                onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
              />
            </Form.Group>

            {/* Help Text */}
            <Form.Group className="mb-3">
              <Form.Label>
                Help Text 
                <small className="text-muted">(optional)</small>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Additional guidance for the user..."
                value={formData.help_text}
                onChange={(e) => setFormData(prev => ({ ...prev, help_text: e.target.value }))}
              />
            </Form.Group>

            {/* Debug info in development */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mb-3">
                <summary className="text-muted small">Debug Info</summary>
                <pre className="small bg-light p-2 mt-2">
                  {JSON.stringify(formData, null, 2)}
                </pre>
              </details>
            )}

            {/* Show general errors */}
            {Object.keys(errors).length > 0 && (
              <Alert variant="danger">
                Please fix the errors above before saving.
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        {step === 2 && (
          <Button variant="primary" onClick={handleSave}>
            {isEditing ? 'Update Question' : 'Save Question'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}